'use client'
import { Box, Flex, Group, Skeleton, Tabs, Text } from '@mantine/core'
import { useResizeObserver } from '@mantine/hooks'
import { useTranslations } from 'next-intl'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import CoinsAICard from '@/app/[locale]/price/[slug]/_components/NewsCommunity/CoinsAICard'
import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import CarouselController from '@/components/Swiper/CarouselController'
import CarouselSlides from '@/components/Swiper/CarouselSlides'
import { useIsResponsiveMobileView } from '@/hooks/responsive'
import { useCoinsAILatestNewsContent } from '@/services/coin/coins'
import {
  Article,
  Reddit,
  RedditWithOriginUrl,
  SocialMediaType,
  SocialsType,
  Tweet,
  useSocialData,
  useSocialNews,
  useSocialVideos,
} from '@/services/coin/social'
import { SummaryLexical } from '@/types/coin'
import { hasHorizontalOverflow } from '@/utils/dom'

import ArticleCard from './ArticleCard'
import OverallAllCard from './OverallAllCard'
import RedditCard from './Reddit/RedditCard'
import styles from './styles.module.css'
import { pipeArticle, pipeVideo } from './utils'
import VideoCard from './VideoCard'

// Define type for allList items
type AllListItem = {
  type: SocialsType | SocialMediaType
  id: number
  data?: Reddit | Article | Tweet | SummaryLexical
}

// Define union type for all possible swiper items
type SwiperItem = AllListItem

interface NewsCommunityProps {
  slug: string
}

function NewsCommunityFallback() {
  return (
    <Box mt={80} style={{ overflow: 'hidden' }}>
      {/* Title and controller area */}
      <Flex justify="space-between" align="center" mb={16}>
        <Skeleton
          h={32}
          w={160}
          style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
        />
      </Flex>

      {/* Tabs area */}
      <Box mb={16} style={{ overflow: 'hidden' }}>
        <Group gap={8} wrap="nowrap">
          {[0, 1, 2, 3].map(index => (
            <Skeleton
              key={index}
              h={36}
              w={80}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: 40,
                flexShrink: 0,
              }}
            />
          ))}
        </Group>
      </Box>

      {/* Content cards area */}
      <Box style={{ overflow: 'hidden' }}>
        <Group gap="md" wrap="nowrap">
          {[0, 1, 2, 3].map(index => (
            <Skeleton
              key={index}
              h={240}
              w={190}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: 16,
                flexShrink: 0,
              }}
            />
          ))}
        </Group>
      </Box>
    </Box>
  )
}

function NewsCommunityContent({ slug }: NewsCommunityProps) {
  const [tab, setTab] = useState<SocialsType | SocialMediaType>(SocialsType.All)
  const [activeIndex, setActiveIndex] = useState(0)
  const [controllerVisible, setControllerVisible] = useState(false)
  const emblaRef = useRef<any>(null)
  const isMobile = useIsResponsiveMobileView()
  const DEFAULT_SLIDES_COUNT = isMobile ? 2 : 4
  const { coin } = useCoinContext()
  const t = useTranslations('newsCommunity')

  const [containerRef, rect] = useResizeObserver()

  // Fetch data
  //fetch overall data
  const { data: overallSocialData } = useSocialData(slug)

  const { totalRedditData, totalTwitterData, hasSocialData } = useMemo(() => {
    const totalRedditData = overallSocialData?.reddit?.followers
    const totalTwitterData = overallSocialData?.twitter?.followers
    const hasSocialData = totalRedditData || totalTwitterData
    return { totalRedditData, totalTwitterData, hasSocialData }
  }, [overallSocialData])

  const { data } = useCoinsAILatestNewsContent(slug)

  const { hasCoinsAILatestNews, coinsAILatestNewsContent } = useMemo(() => {
    // full parent chain check
    const coinsAILatestNewsContent: SummaryLexical | null | undefined =
      data?.latestNewsPage?.tldr?.summaryLexical

    const hasCoinsAILatestNews = coinsAILatestNewsContent != null

    return { hasCoinsAILatestNews, coinsAILatestNewsContent }
  }, [data])

  // fetch radiit data
  const { data: socialData } = useSocialNews(coin.token_id)
  const redditData = useMemo(
    () =>
      socialData.reddit_posts?.map(data => ({
        type: SocialsType.Reddit,
        ...data,
        originUrl: `https://www.reddit.com${data.url}`,
      })) || [],
    [socialData.reddit_posts],
  )

  const tweetData = useMemo(
    () =>
      socialData.tweets?.map(data => ({
        type: SocialsType.Twitter,
        ...data,
        originUrl: `https://twitter.com/${data.alias}/status/${data.id}`,
      })) || [],
    [socialData.tweets],
  )

  // Combine redditData and tweetData alternately
  const mediaSocialData = useMemo(() => {
    const result = []
    const maxLength = Math.max(redditData.length, tweetData.length)

    for (let i = 0; i < maxLength; i++) {
      if (redditData[i]) result.push(redditData[i])
      if (tweetData[i]) result.push(tweetData[i])
    }

    return result
  }, [redditData, tweetData])

  // fetch videos and articles data
  const {
    data: { videos: socialVideos, articles: socialArticles },
  } = useSocialVideos(coin.token_id, 1, 100, SocialMediaType.Video)

  const [videos, articles] = useMemo(() => {
    const pipeVideos = socialVideos?.map(pipeVideo) || []
    const pipeArticles = (socialArticles?.map(pipeArticle) || []).filter(
      (article: Article, index: number, self: Article[]) =>
        index === self.findIndex(a => a.title === article.title),
    )

    return [
      pipeVideos.sort(
        (a: Article, b: Article) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime(),
      ),
      pipeArticles.sort(
        (a: Article, b: Article) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime(),
      ),
    ]
  }, [socialVideos, socialArticles])

  const getAvailableTabs = useMemo(() => {
    const availableTabs = []

    if (
      hasSocialData ||
      redditData?.length ||
      videos?.length ||
      articles?.length ||
      hasCoinsAILatestNews
    ) {
      availableTabs.push({ label: t('tabs.all'), value: SocialsType.All })
    }
    if (mediaSocialData.length > 0) {
      availableTabs.push({ label: t('tabs.socials'), value: SocialsType.Reddit })
    }
    if (articles.length > 0) {
      availableTabs.push({ label: t('tabs.articles'), value: SocialMediaType.Articles })
    }
    if (videos.length > 0) {
      availableTabs.push({ label: t('tabs.videos'), value: SocialMediaType.Video })
    }

    return availableTabs
  }, [hasSocialData, redditData, videos, articles, mediaSocialData, t, hasCoinsAILatestNews])

  useEffect(() => {
    if (getAvailableTabs.length > 0 && !getAvailableTabs.find(t => t.value === tab)) {
      setTab(getAvailableTabs[0].value)
    }
  }, [getAvailableTabs, tab])

  const allList: AllListItem[] = useMemo(() => {
    const list: AllListItem[] = []
    if (hasSocialData) list.push({ type: SocialsType.All, id: 1 })
    if (hasCoinsAILatestNews)
      list.push({ type: SocialsType.CoinsAI, data: coinsAILatestNewsContent, id: 2 })
    if (redditData?.length) {
      list.push({ type: SocialsType.Reddit, id: 3, data: redditData[0] })
    }
    if (videos?.length) {
      list.push({ type: SocialMediaType.Video, id: 4, data: videos[0] })
    }
    if (articles?.length) {
      list.push({ type: SocialMediaType.Articles, id: 5, data: articles[0] })
    }
    return list
  }, [hasSocialData, redditData, videos, articles, hasCoinsAILatestNews, coinsAILatestNewsContent])

  // Render swiper list
  const swiperList = useMemo(() => {
    if (tab === SocialsType.All) return allList
    if (tab === SocialsType.Reddit)
      return mediaSocialData.map((item, index) => ({ data: item, type: item.type, id: index }))
    if (tab === SocialMediaType.Articles)
      return articles.map((item, index) => ({
        data: item,
        type: SocialMediaType.Articles,
        id: index,
      }))
    if (tab === SocialMediaType.Video)
      return videos.map((item, index) => ({ data: item, type: SocialMediaType.Video, id: index }))
    return []
  }, [tab, allList, redditData, articles, videos, mediaSocialData])

  const totalSlides = useMemo(() => swiperList.length, [swiperList])

  // Check if controller should be shown based on horizontal overflow
  const checkControllerVisibility = useCallback(() => {
    if (containerRef.current) {
      // Find the carousel container within our component
      const carouselContainer = containerRef.current.querySelector('.mantine-Carousel-container')
      if (carouselContainer) {
        const shouldShow = hasHorizontalOverflow(carouselContainer as HTMLElement)
        setControllerVisible(shouldShow)
      }
    }
  }, [totalSlides])

  // Check visibility when container size changes using Mantine's useResizeObserver
  useEffect(() => {
    checkControllerVisibility()
  }, [rect, checkControllerVisibility])

  const handlePrev = useCallback(() => {
    if (activeIndex > 0) {
      const newIndex = activeIndex - 1
      setActiveIndex(newIndex)
      emblaRef.current?.scrollTo(newIndex)
    }
  }, [activeIndex])

  const handleNext = useCallback(() => {
    if (activeIndex < totalSlides - 1) {
      const newIndex = activeIndex + 1
      setActiveIndex(newIndex)
      emblaRef.current?.scrollTo(newIndex)
    }
  }, [activeIndex, totalSlides])

  const handleTabChange = useCallback((value: string | null) => {
    if (value) {
      setTab(value as SocialsType | SocialMediaType)
      setActiveIndex(0)
      emblaRef.current?.scrollTo(0)
    }
  }, [])

  const renderSwiper = useCallback(
    (item: SwiperItem) => {
      if (item.type === SocialsType.All)
        return <OverallAllCard redditData={totalRedditData} twitterData={totalTwitterData} />
      if (item.type === SocialsType.CoinsAI && item.data)
        return <CoinsAICard data={item.data as SummaryLexical} key={item.id} slug={slug} />
      if (item.type === SocialsType.Reddit && item.data)
        return <RedditCard data={item.data as RedditWithOriginUrl} key={item.id} />
      if (item.type === SocialMediaType.Video && item.data)
        return <VideoCard video={item.data as Article} key={item.id} />
      if (item.type === SocialMediaType.Articles && item.data)
        return <ArticleCard article={item.data as Article} key={item.id} />
      return null
    },
    [totalRedditData, totalTwitterData, slug],
  )

  const handleSlideChange = useCallback((index: number) => {
    setActiveIndex(index)
  }, [])

  const handleEmblaApi = useCallback((embla: any) => {
    emblaRef.current = embla
  }, [])

  if (getAvailableTabs.length === 0) return null

  return (
    <Box ref={containerRef} mt={80} style={{ overflow: 'visible' }}>
      <CarouselController
        title={t('title', { coinName: coin.name })}
        isPrevDisabled={activeIndex === 0}
        isNextDisabled={!controllerVisible || activeIndex > totalSlides - DEFAULT_SLIDES_COUNT}
        onPrev={handlePrev}
        onNext={handleNext}
        controllerVisible={controllerVisible}
      />
      <Box>
        <Tabs variant="none" value={tab} onChange={handleTabChange}>
          <Tabs.List className={styles.list}>
            {getAvailableTabs.map(t => (
              <Tabs.Tab key={t.value} value={t.value} className={styles.tab}>
                <Text variant="subtitle-emphasis">{t.label}</Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel className={styles.panel} value={tab}>
            <CarouselSlides
              swiperList={swiperList}
              renderSwiper={renderSwiper}
              onSlideChange={handleSlideChange}
              getEmblaApi={handleEmblaApi}
            />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Box>
  )
}

function NewsCommunity({ slug }: NewsCommunityProps) {
  return (
    <Suspense fallback={<NewsCommunityFallback />}>
      <NewsCommunityContent slug={slug} />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(NewsCommunity)
